import { Space, notification, Modal } from "antd";
import React, { useEffect, useState } from "react";
import MobHeader from "../../Components/Header/MobHeader";
import { ThemeFilterBtn, ThemeMainWrapper, ThemeModal } from "../Styles/Theme";
import { useNavigate, Navigate } from "react-router-dom";
import Squares from "../Dashboard/Squares";
import ChatBox from "../../Components/ChatBox";
import { connect } from "react-redux";
import { createSurveyApi, createSurveyReset } from "../../store/actions/surveyAction";
import { uploadSingleFile, deleteFiles } from "../../services/fileUploadService";
import { LaunchHeader, LaunchSurface, LaunchSurfaceContent, PromptSection } from "./LaunchSurvey.style";
import { useSurveyPermissions } from "../../utils/permissions";
import axios from "axios";
import config from "../../config";
import { getUserToken } from "../../utils/helpers";

const LaunchSurvey = ({ createSurveyState, createSurveyApi, createSurveyReset, user }) => {
  const navigate = useNavigate();

  // Permission check - shortcut hook (must be before any conditional returns)
  const { can } = useSurveyPermissions();

  // Read folder context from URL params (when creating from inside a folder)
  const urlParams = new URLSearchParams(window.location.search);
  const createInFolderId = urlParams.get('folderId') || null;
  const createInFolderName = urlParams.get('folderName') || '';

  const [projectDescription, setProjectDescription] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedFileMetadata, setUploadedFileMetadata] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleFileSelect = async (file) => {
    setSelectedFile(file);
    setIsUploading(true);
    setUploadProgress(0);

    try {
      const userId = user?._id || user?.id;
      const result = await uploadSingleFile(
        file,
        'survey-builder',
        userId,
        { isPublic: false },
        (progress) => {
          setUploadProgress(progress);
        }
      );

      setUploadedFileMetadata({
        fileId: result.fileId,
        fileKey: result.fileKey,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
      });

      notification.success({
        message: "File uploaded successfully",
      });
    } catch (error) {
      notification.error({
        message: "Failed to upload file",
        description: error.response?.data?.message || error.message || "Please try again.",
      });
      setSelectedFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileRemove = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteModal(false);

    if (uploadedFileMetadata?.fileId) {
      try {
        await deleteFiles([uploadedFileMetadata.fileId]);
        notification.success({
          message: 'File removed successfully',
        });
      } catch (error) {
        console.error('Error deleting file:', error);
        notification.error({
          message: 'Failed to delete file from storage',
          description: 'The file was removed from the form but may still exist in storage.',
        });
      }
    }

    setSelectedFile(null);
    setUploadedFileMetadata(null);
    setUploadProgress(0);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handleCreateSurvey = (messageText) => {
    if (!messageText || !messageText.trim()) {
      notification.error({
        message: "Please describe your project before continuing.",
      });
      return;
    }

    const payload = {
      title: "Untitled Survey",
      initialMessage: {
        text: messageText.trim(),
      },
    };

    if (uploadedFileMetadata) {
      payload.initialMessage.attachments = [
        {
          file_id: uploadedFileMetadata.fileId,
          file_key: uploadedFileMetadata.fileKey,
        },
      ];
    }

    createSurveyApi(payload);
  };

  useEffect(() => {
    if (createSurveyState.apiState === "success") {
      const surveyId = createSurveyState.data?.data?.survey_id;
      // If created from inside a folder, add to folder (fire-and-forget)
      if (createInFolderId && surveyId) {
        axios.post(
          `${config.api.base_url}/folders/${createInFolderId}/add-item`,
          { itemId: surveyId },
          { headers: { Authorization: 'Bearer ' + getUserToken() } }
        ).catch(() => {});
      }
      if (surveyId) {
        navigate(`/surveys/study/${surveyId}`);
      }
      createSurveyReset();
      setProjectDescription("");
      setSelectedFile(null);
      setUploadedFileMetadata(null);
      setUploadProgress(0);
    }
    if (createSurveyState.apiState === "error") {
      notification.error({
        message: createSurveyState.message || "Unable to create survey",
      });
      createSurveyReset();
    }
  }, [createSurveyState, createSurveyReset, navigate]);

  // Permission check - redirect if no create permission (must be after all hooks)
  if (!can('create')) {
    return <Navigate to="/surveys" replace />;
  }

  return (
    <>
      <MobHeader />
      <ThemeMainWrapper
        className="hasHeader noBackground"
        title={
          <Space>
            <ThemeFilterBtn
              classNames="btnGrayBorder"
              onClick={() => {
                if (createInFolderId) {
                  navigate(`/surveys?folderId=${createInFolderId}&folderName=${encodeURIComponent(createInFolderName)}`);
                } else {
                  navigate(-1);
                }
              }}
              size="large"
              icon={<img src="/images/back-icon.svg" alt="Back" />}
            />
            Launch a Survey
          </Space>
        }
      >
        <LaunchSurface>          
            <LaunchSurfaceContent>
              <LaunchHeader>
                <h1>Launch a Survey</h1>
              </LaunchHeader>
              <PromptSection>
                <ChatBox
                  placeholder="Enter your project in detail."
                  value={projectDescription}
                  onChange={(e) => setProjectDescription(e.target.value)}
                  onSend={handleCreateSurvey}
                  isSending={createSurveyState.apiState === "loading"}
                  minHeight={80}
                  enableFileUpload
                  uploadedFile={selectedFile}
                  onFileSelect={handleFileSelect}
                  onFileRemove={handleFileRemove}
                  uploadProgress={uploadProgress}
                  isUploading={isUploading}
                  acceptedFileTypes=".pdf,.txt,.doc,.docx,.csv,.xls,.xlsx"
                  maxFileSize={10 * 1024 * 1024}
                  attachmentLabel="Upload Discussion Guide"
                  className="customChatBox"
                />
              </PromptSection>
            </LaunchSurfaceContent>          
        </LaunchSurface>
      </ThemeMainWrapper>

      <ThemeModal
        title="Remove File"
        open={showDeleteModal}
        onOk={handleConfirmDelete}
        onCancel={handleCancelDelete}
        okText="Yes, Remove"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        centered
      >
        <p>Are you sure you want to remove this file? This action cannot be undone.</p>
      </ThemeModal>
    </>
  );
};

const mapStateToProps = (state) => ({
  createSurveyState: state.survey.createSurveyState,
  user: state.login.user,
});

const mapDispatchToProps = {
  createSurveyApi,
  createSurveyReset,
};

export default connect(mapStateToProps, mapDispatchToProps)(LaunchSurvey);
